import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Award,
  Compass,
  Map,
  TrendingUp,
  Target,
  Lightbulb,
  Sparkles,
  CheckCircle,
  Clock,
  ArrowRight,
  BarChart3,
  BookOpen,
  Zap,
  Calendar,
  Trophy,
  Brain,
  MessageSquare,
  MessageCircle,
  Feather,
  Shield
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import Card from '../components/shared/Card';
import Button from '../components/shared/Button';
import GamificationWidget from '../components/gamification/GamificationWidget';
import { getTipOfTheDay } from '../data/philosophyTips';
import { getChallengeOfTheWeek } from '../data/philosophyChallenges';
import { getAllModules } from '../data/modules';
import { useGamification } from '../stores/gamificationStore';
import { PROFILES } from '../data/assessmentProfiles';
import { Capacitor } from '@capacitor/core';
import { FALLACIES, EXAMPLES } from '../data/fallacies';

const DashboardPage = () => {
  const navigate = useNavigate();
  const { assessmentResults, moduleProgress, toolsUsage } = useApp();
  const { completeWeeklyChallenge, completedWeeklyChallenges } = useGamification();

  // Fallacy Detective progress. The tool tracked accuracy but kept it to
  // itself, so returning readers had no reminder that a run was in progress.
  const [fallacyProgress] = useState(() => {
    const read = (key, prop) => {
      try {
        const raw = localStorage.getItem(key);
        return raw ? JSON.parse(raw)[prop] : null;
      } catch (e) {
        return null;
      }
    };
    const stats = read('fallacyDetectiveStats', 'stats') || {};
    const seen = read('fallacySeenExamples', 'seen') || [];
    const attempted = FALLACIES.filter(f => stats[f.id]?.attempts);
    const mastered = attempted.filter(f => (
      stats[f.id].attempts >= 3 && stats[f.id].correct / stats[f.id].attempts >= 0.8
    ));
    const totals = attempted.reduce((acc, f) => ({
      correct: acc.correct + stats[f.id].correct,
      attempts: acc.attempts + stats[f.id].attempts
    }), { correct: 0, attempts: 0 });
    // The fallacy most worth practising: lowest accuracy over enough tries.
    const weakest = attempted
      .filter(f => stats[f.id].attempts >= 2)
      .sort((a, b) => (stats[a.id].correct / stats[a.id].attempts) - (stats[b.id].correct / stats[b.id].attempts))[0];
    return {
      seenCount: Array.isArray(seen) ? seen.length : 0,
      poolSize: EXAMPLES.length,
      attempted: attempted.length,
      mastered: mastered.length,
      accuracy: totals.attempts ? Math.round((totals.correct / totals.attempts) * 100) : 0,
      totalAttempts: totals.attempts,
      weakest: weakest || null
    };
  });

  // Get daily tip and weekly challenge
  const tipOfDay = getTipOfTheDay();
  const weeklyChallenge = getChallengeOfTheWeek();

  // Weekly challenge tracking (now using gamification store)
  const [weeklyChallengeStatus, setWeeklyChallengeStatus] = useState(() => {
    const stored = localStorage.getItem('profoundPhilosophy_weeklyChallenges');
    return stored ? JSON.parse(stored) : {};
  });

  const currentChallengeId = weeklyChallenge.id;
  const isCompleted = completedWeeklyChallenges.includes(currentChallengeId);
  const challengeState = isCompleted ? 'completed' :
                         weeklyChallengeStatus[currentChallengeId] || 'not_started';

  const handleChallengeAction = () => {
    let newState;
    if (challengeState === 'not_started') {
      newState = 'accepted';
      const updated = {
        ...weeklyChallengeStatus,
        [currentChallengeId]: newState
      };
      setWeeklyChallengeStatus(updated);
      localStorage.setItem('profoundPhilosophy_weeklyChallenges', JSON.stringify(updated));
    } else if (challengeState === 'accepted') {
      // Mark as completed and award points through gamification
      completeWeeklyChallenge(currentChallengeId, weeklyChallenge.difficulty);

      const updated = {
        ...weeklyChallengeStatus,
        [currentChallengeId]: 'completed'
      };
      setWeeklyChallengeStatus(updated);
      localStorage.setItem('profoundPhilosophy_weeklyChallenges', JSON.stringify(updated));
    } else {
      return; // Already completed
    }
  };

  // (The clone-era "Growth Journey" panel read an assessment shape this app
  // never produces — the assessment's own results view handles retake growth.)

  // Assessment card data: the hub-synced result is thin (id + name + tagline);
  // the archetype detail comes from PROFILES, and the eight score bars from
  // the assessment's own local history (device-only, gracefully absent).
  const topProfile = assessmentResults
    ? (PROFILES[assessmentResults.topProfileId]
        || Object.values(PROFILES).find(p => p.title === assessmentResults.profileType?.name)
        || null)
    : null;
  const localScores = (() => {
    try {
      const history = JSON.parse(localStorage.getItem('philosophicalProfileResults'));
      return Array.isArray(history) && history[0]?.allScores ? history[0].allScores : null;
    } catch { return null; }
  })();
  const leastExplored = localScores ? localScores.slice(-2).reverse() : [];
  const moduleForProfile = (profile) => {
    const order = Number(profile?.module?.match(/Module (\d+)/)?.[1]);
    return getAllModules().find(m => m.order === order) || null;
  };

  // Calculate overall progress
  const calculateOverallProgress = () => {
    const moduleIds = Object.keys(moduleProgress || {});
    if (moduleIds.length === 0) return 0;
    const completedCount = moduleIds.filter(id => moduleProgress[id]?.completed).length;
    return Math.round((completedCount / 8) * 100); // Assuming 8 modules total
  };

  // Calculate tool usage stats
  const calculateToolStats = () => {
    const toolIds = Object.keys(toolsUsage || {});
    const totalUses = toolIds.reduce((sum, id) => sum + (toolsUsage[id]?.uses || 0), 0);
    return {
      toolsExplored: toolIds.length,
      totalUses
    };
  };

  const toolStats = calculateToolStats();
  const overallProgress = calculateOverallProgress();

  // (The clone-era "module challenges" section read module.conversationChallenges,
  // a field no philosophy module defines — computation and hidden UI removed 2026-08-16.)

  // Keyboard operability for the clickable Quick Action cards (WCAG 2.1.1)
  const cardKeyNav = (path) => (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      navigate(path);
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Beginner': return 'text-green-400 bg-green-400/10 border-green-400/30';
      case 'Intermediate': return 'text-amber-400 bg-amber-400/10 border-amber-400/30';
      case 'Advanced': return 'text-red-300 bg-red-400/10 border-red-400/30';
      default: return 'text-blue-400 bg-blue-400/10 border-blue-400/30';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 pb-4 md:pb-8 pt-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Hero: mid-morning sky backdrop, the working day of thinking */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative text-center mb-8 rounded-3xl overflow-hidden py-10 px-4"
        >
          <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
            <img
              src="/images/cloud-dashboard.webp"
              alt=""
              className="w-full h-full object-cover object-[50%_35%] opacity-60"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-gunmetal/55 via-gunmetal/30 to-gunmetal" />
          </div>
          <div className="relative">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="mb-6 inline-flex items-center justify-center w-28 h-28 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-500 mx-auto"
            >
              <Brain className="w-14 h-14 text-white" />
            </motion.div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-2">
              Your Philosophy Journey
            </h1>
            <p className="text-xl text-slate-200">
              Track your progress through the modules, tools and thinking challenges
            </p>
          </div>
        </motion.div>

        {/* Tip of the Day & Weekly Challenge */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Tip of the Day */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="p-6 h-full bg-gradient-to-br from-teal-500/5 to-blue-500/5 border-teal-500/20">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-blue-500 rounded-xl flex items-center justify-center">
                  <Lightbulb className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-white">Today's Philosophy Tip</h2>
                  <span className="text-xs text-teal-300">{tipOfDay.category}</span>
                </div>
              </div>
              <div className="space-y-3">
                <p className="text-slate-200 leading-relaxed">{tipOfDay.tip}</p>
                <div className="pt-3 border-t border-teal-500/20">
                  <p className="text-sm text-slate-300">
                    <span className="font-semibold text-teal-300">Try this: </span>
                    {tipOfDay.actionable}
                  </p>
                </div>
              </div>
            </Card>
          </motion.div>

          {/* Weekly Challenge */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="p-6 h-full bg-gradient-to-br from-orange-500/5 to-red-500/5 border-orange-500/20">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-12 h-12 shrink-0 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center">
                    <Target className="w-6 h-6 text-white" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-lg font-bold text-white">This Week's Challenge</h3>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-xs text-orange-300">{weeklyChallenge.category}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${getDifficultyColor(weeklyChallenge.difficulty)}`}>
                        {weeklyChallenge.difficulty}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Trophy className="w-4 h-4 text-amber-400" />
                  <span className="text-amber-400 font-bold">{weeklyChallenge.points} pts</span>
                </div>
              </div>
              <div className="space-y-3">
                <h4 className="text-xl font-semibold text-white">{weeklyChallenge.title}</h4>
                <p className="text-slate-200 text-sm leading-relaxed">{weeklyChallenge.description}</p>
                <div className="pt-3 border-t border-orange-500/20">
                  <p className="text-sm text-slate-300">
                    <span className="font-semibold text-orange-300">Why it matters: </span>
                    {weeklyChallenge.why}
                  </p>
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <Clock className="w-3 h-3" />
                  <span>{weeklyChallenge.timeCommitment}</span>
                </div>
                <Button
                  size="sm"
                  variant={challengeState === 'completed' ? 'primary' : 'secondary'}
                  className="w-full mt-2"
                  onClick={handleChallengeAction}
                  disabled={challengeState === 'completed'}
                >
                  {challengeState === 'not_started' && 'Accept Challenge'}
                  {challengeState === 'accepted' && 'Mark Complete'}
                  {challengeState === 'completed' && (
                    <span className="flex items-center gap-2 justify-center">
                      <CheckCircle className="w-4 h-4" />
                      Completed
                    </span>
                  )}
                </Button>
              </div>
            </Card>
          </motion.div>
        </div>

        {/* Gamification Widget */}
        <GamificationWidget />

        {/* Assessment Summary, derived from the thin hub-synced result via
            PROFILES (static), with the eight-way score bars from the local
            assessment history when this device has one. */}
        {assessmentResults ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-8"
          >
            <Card className="p-6 bg-gradient-to-br from-purple-500/10 to-pink-500/10 border-purple-500/20">
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center">
                    <Award className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white mb-1">
                      {assessmentResults.profileType?.name || 'Your Thinking Profile'}
                    </h2>
                    <p className="text-purple-200">
                      {assessmentResults.profileType?.tagline || `Overall Score: ${assessmentResults.avgScore || 0}/100`}
                    </p>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => navigate('/assessment')}
                >
                  Retake
                </Button>
              </div>

              {/* Eight-way leanings, from this device's assessment history */}
              {localScores && (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-purple-300 uppercase mb-4 flex items-center gap-2">
                    <BarChart3 className="w-4 h-4" />
                    Your Philosophical Leanings
                  </h3>
                  <div className="space-y-3">
                    {localScores.map((entry) => {
                      const Icon = PROFILES[entry.id]?.icon;
                      return (
                        <div key={entry.id} className="space-y-1">
                          <div className="flex items-center justify-between text-sm">
                            <div className="flex items-center gap-2">
                              {Icon && <Icon className="w-4 h-4 text-purple-300" />}
                              <span className="text-slate-200 font-medium">{entry.title}</span>
                            </div>
                            <span className="text-purple-300 font-bold">{entry.percentage}%</span>
                          </div>
                          <div className="w-full bg-slate-700 rounded-full h-2">
                            <div
                              className="h-2 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full transition-all"
                              style={{ width: `${entry.percentage}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Strengths & the questions that drive this archetype */}
              {topProfile && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h3 className="text-sm font-semibold text-purple-300 uppercase mb-3 flex items-center gap-2">
                      <Sparkles className="w-4 h-4" />
                      Your Strengths
                    </h3>
                    <div className="space-y-2">
                      {topProfile.strengths.slice(0, 4).map((strength, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm text-slate-300">
                          <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                          {strength}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-amber-300 uppercase mb-3 flex items-center gap-2">
                      <Target className="w-4 h-4" />
                      Questions That Drive You
                    </h3>
                    <div className="space-y-2">
                      {topProfile.keyQuestions.slice(0, 3).map((q, idx) => (
                        <div key={idx} className="flex items-start gap-2 text-sm text-slate-300">
                          <TrendingUp className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                          {q}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Where to go with it */}
              {topProfile && (
                <div className="pt-6 border-t border-purple-500/20">
                  <h3 className="text-sm font-semibold text-white uppercase mb-3 flex items-center gap-2">
                    <ArrowRight className="w-4 h-4" />
                    Where to Take It
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {moduleForProfile(topProfile) && (
                      <button
                        onClick={() => navigate(`/modules/${moduleForProfile(topProfile).id}`)}
                        className="flex items-start gap-3 p-3 bg-slate-800/50 rounded-lg text-left hover:bg-slate-700/50 transition-colors"
                      >
                        <BookOpen className="w-5 h-5 text-conversation-teal flex-shrink-0 mt-0.5" />
                        <span className="text-slate-300 text-sm flex-1">
                          Your home module: <span className="text-white font-medium">{moduleForProfile(topProfile).title}</span>
                        </span>
                      </button>
                    )}
                    <button
                      onClick={() => navigate('/toolkit')}
                      className="flex items-start gap-3 p-3 bg-slate-800/50 rounded-lg text-left hover:bg-slate-700/50 transition-colors"
                    >
                      <Zap className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-300 text-sm flex-1">
                        Your tools: <span className="text-white font-medium">{topProfile.recommendedTools.join(' · ')}</span>
                      </span>
                    </button>
                  </div>
                  {leastExplored.length > 0 && (
                    <p className="text-xs text-slate-400 mt-3">
                      Least explored so far:{' '}
                      {leastExplored.map((e, i) => {
                        const mod = moduleForProfile(PROFILES[e.id]);
                        return (
                          <span key={e.id}>
                            {i > 0 && ' · '}
                            {mod ? (
                              <button onClick={() => navigate(`/modules/${mod.id}`)} className="underline decoration-dotted hover:text-white transition-colors">
                                {e.title}
                              </button>
                            ) : e.title}
                          </span>
                        );
                      })}
                      , the domains with the most left to surprise you.
                    </p>
                  )}
                </div>
              )}
            </Card>
          </motion.div>
        ) : Capacitor.isNativePlatform() ? (
          // In the iOS app the Dashboard is the home screen, so a newcomer's first
          // prompt is the app's front door, the assessment, not the Toolkit.
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-8"
          >
            <Card className="p-8 text-center bg-gradient-to-br from-purple-500/5 to-pink-500/5 border-purple-500/20">
              <Brain className="w-16 h-16 text-purple-400 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-white mb-2">
                Discover Your Philosophical Profile
              </h3>
              <p className="text-slate-300 mb-6 max-w-md mx-auto">
                Eighteen questions, about five minutes: find out which of philosophy's great questions you're already drawn to, and where to begin.
              </p>
              <div className="flex justify-center">
                <Button
                  onClick={() => navigate('/assessment')}
                  className="inline-flex items-center gap-2"
                >
                  <span>Take the Assessment</span>
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            </Card>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-8"
          >
            <Card className="p-8 text-center bg-gradient-to-br from-purple-500/5 to-pink-500/5 border-purple-500/20">
              <Brain className="w-16 h-16 text-purple-400 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-white mb-2">
                Put Your Thinking to Work
              </h3>
              <p className="text-slate-300 mb-6 max-w-md mx-auto">
                The Philosopher's Toolkit holds eighteen interactive tools: question your beliefs Socratically, spot fallacies, test moral dilemmas through three ethical lenses, and more.
              </p>
              <div className="flex justify-center">
                <Button
                  onClick={() => navigate('/toolkit')}
                  className="inline-flex items-center gap-2"
                >
                  <span>Explore the Toolkit</span>
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            </Card>
          </motion.div>
        )}


        {fallacyProgress.totalAttempts > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="mb-8"
          >
            <Card className="p-6">
              <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
                <div className="flex items-start gap-3 min-w-0">
                  <Shield className="w-6 h-6 text-blue-400 flex-shrink-0 mt-1" />
                  <div className="min-w-0">
                    <h3 className="text-xl font-bold text-white">Fallacy Detective</h3>
                    <p className="text-sm text-slate-400">
                      {fallacyProgress.accuracy}% accuracy over {fallacyProgress.totalAttempts} answer{fallacyProgress.totalAttempts === 1 ? '' : 's'}
                    </p>
                  </div>
                </div>
                <Button
                  onClick={() => navigate('/toolkit/fallacy-detective')}
                  className="inline-flex items-center gap-2 flex-shrink-0"
                >
                  <span>Keep training</span>
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </div>

              <div className="grid sm:grid-cols-3 gap-4">
                <div className="bg-slate-900/40 rounded-lg p-4 border border-slate-700">
                  <p className="text-2xl font-bold text-white">
                    {fallacyProgress.seenCount}/{fallacyProgress.poolSize}
                  </p>
                  <p className="text-sm text-slate-400">examples seen</p>
                </div>
                <div className="bg-slate-900/40 rounded-lg p-4 border border-slate-700">
                  <p className="text-2xl font-bold text-white">
                    {fallacyProgress.attempted}/{FALLACIES.length}
                  </p>
                  <p className="text-sm text-slate-400">fallacies met</p>
                </div>
                <div className="bg-slate-900/40 rounded-lg p-4 border border-slate-700">
                  <p className="text-2xl font-bold text-emerald-400">{fallacyProgress.mastered}</p>
                  <p className="text-sm text-slate-400">mastered</p>
                </div>
              </div>

              {fallacyProgress.weakest && (
                <p className="text-sm text-slate-300 mt-4">
                  Worth another look: <span className="text-white font-medium">{fallacyProgress.weakest.name}</span> ({fallacyProgress.weakest.description.toLowerCase()}).
                </p>
              )}
            </Card>
          </motion.div>
        )}

        {/* Stats Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8"
        >
          <Card className="p-6">
            <div className="flex items-center gap-3 mb-2">
              <BarChart3 className="w-8 h-8 text-blue-400" />
              <span className="text-slate-400 text-sm">Overall Progress</span>
            </div>
            <div className="text-3xl font-bold text-white">{overallProgress}%</div>
            <div className="mt-3 w-full bg-slate-700 rounded-full h-2">
              <div
                className="h-2 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full transition-all"
                style={{ width: `${overallProgress}%` }}
              />
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center gap-3 mb-2">
              <BookOpen className="w-8 h-8 text-green-400" />
              <span className="text-slate-400 text-sm">Modules Started</span>
            </div>
            <div className="text-3xl font-bold text-white">
              {Object.keys(moduleProgress || {}).length}
            </div>
            <p className="text-sm text-slate-400 mt-1">of 8 total</p>
          </Card>

          <Card className="p-6">
            <div className="flex items-center gap-3 mb-2">
              <Zap className="w-8 h-8 text-amber-400" />
              <span className="text-slate-400 text-sm">Tools Used</span>
            </div>
            <div className="text-3xl font-bold text-white">{toolStats.totalUses}</div>
            <p className="text-sm text-slate-400 mt-1">{toolStats.toolsExplored} tools explored</p>
          </Card>

          <Card className="p-6">
            <div className="flex items-center gap-3 mb-2">
              <Trophy className="w-8 h-8 text-purple-400" />
              <span className="text-slate-400 text-sm">Completed</span>
            </div>
            <div className="text-3xl font-bold text-white">
              {Object.values(moduleProgress || {}).filter(m => m.completed).length}
            </div>
            <p className="text-sm text-slate-400 mt-1">modules finished</p>
          </Card>
        </motion.div>


        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <h2 className="text-2xl font-bold text-white mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card
              className="p-6 cursor-pointer hover:scale-105 transition-transform"
              onClick={() => navigate('/modules')}
              role="button"
              tabIndex={0}
              onKeyDown={cardKeyNav('/modules')}
              aria-label="Continue learning: go to your modules"
            >
              <BookOpen className="w-10 h-10 text-blue-400 mb-3" />
              <h3 className="text-lg font-semibold text-white mb-2">Continue Learning</h3>
              <p className="text-sm text-slate-400">
                Pick up where you left off in your modules
              </p>
              <ArrowRight className="w-5 h-5 text-blue-400 mt-3" />
            </Card>

            <Card
              className="p-6 cursor-pointer hover:scale-105 transition-transform"
              onClick={() => navigate('/toolkit')}
              role="button"
              tabIndex={0}
              onKeyDown={cardKeyNav('/toolkit')}
              aria-label="Use tools: go to the Philosopher's Toolkit"
            >
              <Zap className="w-10 h-10 text-amber-400 mb-3" />
              <h3 className="text-lg font-semibold text-white mb-2">Use Tools</h3>
              <p className="text-sm text-slate-400">
                Practise with the interactive philosophy tools
              </p>
              <ArrowRight className="w-5 h-5 text-amber-400 mt-3" />
            </Card>

            <Card
              className="p-6 cursor-pointer hover:scale-105 transition-transform"
              onClick={() => navigate('/positions')}
              role="button"
              tabIndex={0}
              onKeyDown={cardKeyNav('/positions')}
              aria-label="Your philosophy so far: go to your positions"
            >
              <Compass className="w-10 h-10 text-violet-400 mb-3" />
              <h3 className="text-lg font-semibold text-white mb-2">Your Philosophy, So Far</h3>
              <p className="text-sm text-slate-400">
                The positions you have taken at the great forks, and the tensions between them
              </p>
              <ArrowRight className="w-5 h-5 text-violet-400 mt-3" />
            </Card>

            <Card
              className="p-6 cursor-pointer hover:scale-105 transition-transform"
              onClick={() => navigate('/lineage')}
              role="button"
              tabIndex={0}
              onKeyDown={cardKeyNav('/lineage')}
              aria-label="The Lineage: go to the timeline of thinkers"
            >
              <Map className="w-10 h-10 text-conversation-teal mb-3" />
              <h3 className="text-lg font-semibold text-white mb-2">The Lineage</h3>
              <p className="text-sm text-slate-400">
                Twenty-six centuries of thinkers answering each other; see who you have met
              </p>
              <ArrowRight className="w-5 h-5 text-conversation-teal mt-3" />
            </Card>

            <Card
              className="p-6 cursor-pointer hover:scale-105 transition-transform"
              onClick={() => navigate('/journal')}
              role="button"
              tabIndex={0}
              onKeyDown={cardKeyNav('/journal')}
              aria-label="Go to the Philosopher's Journal"
            >
              <Feather className="w-10 h-10 text-pearl mb-3" />
              <h3 className="text-lg font-semibold text-white mb-2">The Philosopher's Journal</h3>
              <p className="text-sm text-slate-400">
                Every "write it down" lands here, kept private on this device
              </p>
              <ArrowRight className="w-5 h-5 text-pearl mt-3" />
            </Card>

            <Card
              className="p-6 cursor-pointer hover:scale-105 transition-transform"
              onClick={() => navigate('/companion')}
              role="button"
              tabIndex={0}
              onKeyDown={cardKeyNav('/companion')}
              aria-label="Talk to Sofia: go to the companion"
            >
              <MessageCircle className="w-10 h-10 text-bright-gold mb-3" />
              <h3 className="text-lg font-semibold text-white mb-2">Talk to Sofia</h3>
              <p className="text-sm text-slate-400">
                State a belief and have it examined; she teaches generously and never hands over the verdict
              </p>
              <ArrowRight className="w-5 h-5 text-bright-gold mt-3" />
            </Card>

            <Card
              className="p-6 cursor-pointer hover:scale-105 transition-transform"
              onClick={() => navigate('/resources')}
              role="button"
              tabIndex={0}
              onKeyDown={cardKeyNav('/resources')}
              aria-label="Explore resources: go to the resources library"
            >
              <MessageSquare className="w-10 h-10 text-purple-400 mb-3" />
              <h3 className="text-lg font-semibold text-white mb-2">Explore Resources</h3>
              <p className="text-sm text-slate-400">
                Discover articles, videos, and exercises
              </p>
              <ArrowRight className="w-5 h-5 text-purple-400 mt-3" />
            </Card>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default DashboardPage;
